import { flushLocalDb, openLocalDb } from '../db/local.js';

export type CaptureTrayMode = 'separate' | 'single';

export interface StoredCaptureTray<T> {
  mode: CaptureTrayMode;
  items: T[];
  singleDraftId: string | null;
  updatedAt: string;
}

export interface CaptureTrayScope {
  accountId: string;
  tenantId: string;
  businessId: string;
}

export function captureTrayScopeKey(scope: CaptureTrayScope): string {
  return `${scope.accountId}:${scope.tenantId}:${scope.businessId}`;
}

export async function loadCaptureTray<T>(scope: CaptureTrayScope): Promise<StoredCaptureTray<T> | null> {
  const db = await openLocalDb(null);
  const result = await db.query(
    `SELECT mode, items, single_draft_id, updated_at
       FROM document_capture_trays_v1
      WHERE account_id = ? AND tenant_id = ? AND business_id = ?`,
    [scope.accountId, scope.tenantId, scope.businessId],
  );
  const row = result.values?.[0] as {
    mode?: string;
    items?: string;
    single_draft_id?: string | null;
    updated_at?: string;
  } | undefined;
  if (!row) return null;
  try {
    const items = JSON.parse(row.items ?? '[]');
    if (!Array.isArray(items)) return null;
    return {
      mode: row.mode === 'single' ? 'single' : 'separate',
      items: items as T[],
      singleDraftId: row.single_draft_id ?? null,
      updatedAt: row.updated_at ?? '',
    };
  } catch {
    return null;
  }
}

export async function saveCaptureTray<T>(
  scope: CaptureTrayScope,
  snapshot: Omit<StoredCaptureTray<T>, 'updatedAt'>,
): Promise<void> {
  const db = await openLocalDb(null);
  if (snapshot.items.length === 0) {
    await deleteCaptureTray(scope);
    return;
  }
  await db.run(
    `INSERT INTO document_capture_trays_v1(
       account_id, tenant_id, business_id, mode, items, single_draft_id, updated_at
     ) VALUES (?, ?, ?, ?, ?, ?, datetime('now'))
     ON CONFLICT(account_id, tenant_id, business_id) DO UPDATE SET
       mode = excluded.mode,
       items = excluded.items,
       single_draft_id = excluded.single_draft_id,
       updated_at = datetime('now')`,
    [
      scope.accountId,
      scope.tenantId,
      scope.businessId,
      snapshot.mode,
      JSON.stringify(snapshot.items),
      snapshot.singleDraftId,
    ],
  );
  await flushLocalDb();
}

export async function deleteCaptureTray(scope: CaptureTrayScope): Promise<void> {
  const db = await openLocalDb(null);
  await db.run(
    `DELETE FROM document_capture_trays_v1
      WHERE account_id = ? AND tenant_id = ? AND business_id = ?`,
    [scope.accountId, scope.tenantId, scope.businessId],
  );
  await flushLocalDb();
}
